<template>
  <div class="pod-analytics">
    <v-select
      v-model="selectedPeriod"
      variant="outlined"
      density="comfortable"
      :label="$t('routes.pod.analyticsDialog.period')"
      :items="selectablePeriods"
    />
    <v-row v-if="isLoading">
      <v-col cols="12" class="d-flex justify-center">
        <v-progress-circular
          indeterminate
          size="70"
          width="7"
          color="primary"
          :aria-label="$t('common.loading')"
        />
      </v-col>
    </v-row>
    <v-row v-if="refreshAnalyticsError">
      <v-col cols="12">
        <v-alert type="warning" variant="tonal" density="compact" class="mb-2">
          {{ refreshAnalyticsError }}
        </v-alert>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          :loading="isLoading"
          @click="refreshAnalytics"
        >
          {{ $t('routes.pod.analyticsDialog.retry') }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-if="!isLoading" class="d-flex flex-column">
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.totalViews') }}</div>
        <div class="pod-analytics-row-value">{{ totalViews }}</div>
      </div>
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.uniqueViewers') }}</div>
        <div class="pod-analytics-row-value">
          {{ totalUniqueViewers ? totalUniqueViewers : 0 }}
        </div>
      </div>
      <div class="delimiter" />
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.totalDownloads') }}</div>
        <div class="pod-analytics-row-value">{{ totalDownloads }}</div>
      </div>
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.totalComments') }}</div>
        <div class="pod-analytics-row-value">{{ totalComments }}</div>
      </div>
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.totalShares') }}</div>
        <div class="pod-analytics-row-value">{{ totalShares }}</div>
      </div>
      <div class="delimiter" />
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.lastView') }}</div>
        <div class="pod-analytics-row-value">{{ lastView }}</div>
      </div>
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.lastDownload') }}</div>
        <div class="pod-analytics-row-value">{{ lastDownload }}</div>
      </div>
      <div class="pod-analytics-row">
        <div class="pod-analytics-row-label">{{ $t('routes.pod.analyticsDialog.lastComment') }}</div>
        <div class="pod-analytics-row-value">{{ lastComment }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from '@/components/formatters/timestamp.js';
import { subDays, subMonths, subYears } from 'date-fns';
import PodCalls from '@/api/modules/PodCalls.js';

export default {
  name: 'PodAnalytics',
  props: {
    podId: {
      type: String,
      required: true,
    }
  },
  watch: {
    selectedPeriod: function () {
      switch (this.selectedPeriod) {
        case 'today':
          this.periodFrom = formatDate(new Date());
          this.periodTo = formatDate(new Date());
          break;
        case 'last_week':
          this.periodFrom = formatDate(subDays(new Date(), 7));
          this.periodTo = formatDate(new Date());
          break;
        case 'last_month':
          this.periodFrom = formatDate(subMonths(new Date(), 1));
          this.periodTo = formatDate(new Date());
          break;
        case 'last_year':
          this.periodFrom = formatDate(subYears(new Date(), 1));
          this.periodTo = formatDate(new Date());
          break;
      }

      this.refreshAnalytics();
    },
  },
  data() {
    return {
      isLoading: false,
      totalActions: null,
      totalUniqueViewers: null,
      lastActions: null,
      refreshAnalyticsError: null,
      periodFrom: formatDate(new Date()),
      periodTo: formatDate(new Date()),
      selectedPeriod: 'today',
    };
  },
  mounted() {
    this.refreshAnalytics();
  },
  computed: {
    selectablePeriods: function () {
      return [
        { value: 'last_year', title: this.$t('routes.pod.analyticsDialog.periods.lastYear') },
        { value: 'last_month', title: this.$t('routes.pod.analyticsDialog.periods.lastMonth') },
        { value: 'last_week', title: this.$t('routes.pod.analyticsDialog.periods.lastWeek') },
        { value: 'today', title: this.$t('routes.pod.analyticsDialog.periods.today') },
      ];
    },
    totalViews: function () {
      return this.totalActions && this.totalActions.view ? this.totalActions.view : 0;
    },
    totalDownloads: function () {
      return this.totalActions && this.totalActions.download ? this.totalActions.download : 0;
    },
    totalComments: function () {
      return this.totalActions && this.totalActions.comment ? this.totalActions.comment : 0;
    },
    totalShares: function () {
      return this.totalActions && this.totalActions.share ? this.totalActions.share : 0;
    },
    lastView: function () {
      return this.lastActions && this.lastActions.view ? this.lastActions.view : null;
    },
    lastDownload: function () {
      return this.lastActions && this.lastActions.download ? this.lastActions.download : null;
    },
    lastComment: function () {
      return this.lastActions && this.lastActions.comment ? this.lastActions.comment : null;
    }
  },
  methods: {
    async refreshAnalytics() {
      this.isLoading = true;
      this.refreshAnalyticsError = null;

      try {
        const res = await PodCalls.getAnalytics({
          id: this.podId,
          from: this.periodFrom,
          to: this.periodTo,
        });

        this.totalActions = res.data.actionsTotal;
        this.totalUniqueViewers = res.data.uniqueViewersTotal;
        this.lastActions = res.data.lastActions;
      } catch (error) {
        if (error?.response?.status === 503) {
          this.refreshAnalyticsError = this.$t('routes.pod.analyticsDialog.unavailable');
        } else if (error?.response?.data?.error?.message) {
          this.refreshAnalyticsError = error.response.data.error.message;
        } else {
          this.refreshAnalyticsError = this.$t('common.generalError');
          console.error(error);
        }
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.delimiter {
  height: 1rem;
}

.pod-analytics-row {
  display: flex;
  justify-content: space-between;
  flex-direction: row;

  &-label, &-value {
    text-wrap: nowrap;
    text-overflow: ellipsis;
    word-wrap: anywhere;
  }

  &-label {
    width: 50%;
    font-weight: 600;
  }
}
</style>
